import React from 'react';
import { WBFlex, WBSvgIcon, WBTypography } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import {
  FILE_TYPES,
  PDF_FILE_EXTENSIONS,
  SUPPORT_DOCUMENT_EXTENSIONS,
  S3Upload,
} from '@admiin-com/ds-common';
import { WBS3MediaDragDrop as S3MediaDragDrop } from '@admiin-com/ds-amplify-web';
import UploadIcon from '../../../../assets/icons/upload.svg';
import { useTheme } from '@mui/material';
interface TaskUploadProps {
  value?: S3Upload[];
  onChange: (images: S3Upload[]) => void;
  setLoading?: (loading: boolean) => void;
  label?: string;
  isPDFOnly?: boolean;
  isSupportDocuments?: boolean;
  maxFiles?: number;
  includeNameInKey?: boolean;
}

const TaskUpload = React.forwardRef<any, TaskUploadProps>(
  (
    {
      label,
      value,
      onChange,
      isPDFOnly,
      isSupportDocuments,
      setLoading,
      maxFiles = 5,
    },
    ref
  ) => {
    const { t } = useTranslation();

    // Track pre-existing files (from backend) vs session uploads
    const initialFilesRef = React.useRef<S3Upload[]>(value ?? []);
    const [removedKeys, setRemovedKeys] = React.useState<Set<string>>(
      new Set()
    );
    const sessionUploadsRef = React.useRef<S3Upload[]>([]);

    const existingFiles = React.useMemo(
      () =>
        initialFilesRef.current
          .filter((f) => !removedKeys.has(f.key))
          .map((f) => ({ ...f, name: f.name ?? undefined })),
      [removedKeys]
    );

    const handleUploaded = (images: S3Upload[]) => {
      sessionUploadsRef.current = images;
      const existing = initialFilesRef.current.filter(
        (f) => !removedKeys.has(f.key)
      );
      onChange([...existing, ...images]);
      if (setLoading) setLoading(false);
    };

    const handleRemove = (file: S3Upload) => {
      const newRemovedKeys = new Set(removedKeys);
      newRemovedKeys.add(file.key);
      setRemovedKeys(newRemovedKeys);
      const remaining = initialFilesRef.current.filter(
        (f) => !newRemovedKeys.has(f.key)
      );
      onChange([...remaining, ...sessionUploadsRef.current]);
    };

    const FILE_EXTENSIONS = isPDFOnly
      ? [FILE_TYPES.PDF]
      : isSupportDocuments
      ? SUPPORT_DOCUMENT_EXTENSIONS
      : PDF_FILE_EXTENSIONS;
    const extensions = Object.entries(FILE_TYPES)
      .filter(([key, value]) => FILE_EXTENSIONS.includes(value))
      .map(([key, value]) => key)
      .join(', ');

    const theme = useTheme();
    const UploadMessage = (
      <>
        <WBFlex gap={1}>
          <WBSvgIcon fontSize="small" color={theme.palette.primary.main}>
            <UploadIcon />
          </WBSvgIcon>
          <WBTypography
            textAlign={'center'}
            fontWeight={'bold'}
            variant="body2"
          >
            {t(isPDFOnly ? 'uploadDocument' : 'uploadExistingDocuments', {
              ns: 'taskbox',
            })}
          </WBTypography>
        </WBFlex>
        <WBTypography
          color={'text.secondary'}
          textAlign={'center'}
          variant="body2"
          mt={1}
        >
          {`${t('supportedFiles', { ns: 'taskbox' })} ${extensions}`}
        </WBTypography>

        <WBTypography
          color={'text.secondary'}
          textAlign={'center'}
          variant="body2"
        >
          {`${t('maxSize', { ns: 'taskbox', size: '25MB' })}`}
        </WBTypography>
      </>
    );

    return (
      <WBFlex flexDirection={'column'} gap={2} ref={ref}>
        {label && (
          <WBTypography variant="body1" fontWeight="bold">
            {label}
          </WBTypography>
        )}
        <S3MediaDragDrop
          uploadBtnText=""
          validFileTypes={FILE_EXTENSIONS}
          inputAccept={FILE_EXTENSIONS.join(', ')}
          level="private"
          files={existingFiles}
          onRemove={handleRemove}
          onUploaded={handleUploaded}
          onDropped={(files: FileList) => {
            if (setLoading) setLoading(true);
          }}
          uploadMessage={UploadMessage}
          uploadBtnTextProps={{
            variant: 'body2',
            fontWeight: 'bold',
            color: 'text.primary',
          }}
          removeConfirmationTitle={t('removeConfirmationTitle', {
            ns: 'taskbox',
          })}
          removeConfirmationDescription={t('removeConfirmationDescription', {
            ns: 'taskbox',
          })}
          maxFiles={maxFiles}
          includeNameInKey
        />
      </WBFlex>
    );
  }
);

export default TaskUpload;
